/**
 * `/app/account/privacy` — the three elections (`MEM-010`).
 *
 * ## What was already true, and what was missing
 *
 * The elections were **built and enforced** long before this screen: `matching/selection.ts` drops
 * an opted-out member from the candidate pool before any pairing happens (AC-3, bidirectionally),
 * `createEventAttendeesPort` narrows election (a) into `appearsInPreview` (AC-2),
 * `createFollowUpRecipientPort` reads election (c) (AC-5), and
 * `MEMBER_PRIVACY_ELECTION_DEFAULTS` seeds (c) off. What did not exist was any way for a member to
 * **change** them after registration — `saveStep` refuses a finished row, and the only other write
 * was a four-key schema that excluded all three. So the elections were editable at registration
 * and immutable for ever after. This screen and its endpoint are AC-1.
 *
 * ## The statement is rendered, never retyped
 *
 * `BIDIRECTIONAL_OPT_OUT_STATEMENT` is fixed text defined once in `@h2hspark/contracts`,
 * re-exported by `platform-config`, and served on this screen's payload. It is **not a toggle** —
 * it is the sentence that sits where a toggle would, because disabling the rule would breach a
 * member's privacy election and so the platform makes it unrepresentable. A member reading it here
 * and an operator reading it in the admin console are reading the same string.
 *
 * ## The 10 September prototype, minus the sentence it contradicts
 *
 * `Web App.dc.html`'s `showPrivacy` (lines 804–827) is what this draws: the back link, the 4xl
 * title over a 700px lede, one 900px card of three divided rows with a 52×30 switch at the right
 * end of each, the purple `shield-check` band, the notification-scope paragraph and a pill Save.
 *
 * Two of its decisions are **not** taken, both logged as entry 130 of `handoff-corrections.md`:
 * the prototype's toggles apply instantly *and* carry a Save, which cannot both be true, and
 * `MEM-007` AC-4 settles it — nothing on a member surface writes implicitly, so the toggles move a
 * draft and Save is the write. And its rows are plain `<button>`s with no switch role (entry 132),
 * where these are `Switch` at the member density.
 */
import { Card, CardContent, Button, Skeleton, Switch, useErrorToast } from '@h2hspark/ui';
import { ShieldCheck } from 'lucide-react';
import * as React from 'react';

import { PageBackLink } from '@/member/page-back-link';

import { usePrivacyElections, useSavePrivacyElections } from './api';
import { MEMBER_ACCOUNT_COPY, MEMBER_PRIVACY_COPY, MEMBER_PROFILE_ROUTES } from './constants';

type Elections = {
  appearInAttendeePreview: boolean;
  receivePreEventMatchSuggestions: boolean;
  receivePostEventFollowUpNotifications: boolean;
};

export function PrivacyPage(): React.ReactElement {
  const privacy = usePrivacyElections();
  const save = useSavePrivacyElections();
  const showError = useErrorToast();

  const [draft, setDraft] = React.useState<Elections | null>(null);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (draft !== null || privacy.data === undefined) return;
    setDraft({ ...privacy.data.elections });
  }, [draft, privacy.data]);

  React.useEffect(() => {
    if (privacy.error !== null) showError(privacy.error, 'We could not load your settings.');
  }, [privacy.error, showError]);

  if (privacy.isLoading || draft === null || privacy.data === undefined) {
    return <Skeleton className="h-80 w-full" />;
  }

  return (
    <form
      className="pb-8"
      onSubmit={(event) => {
        event.preventDefault();
        // Explicit, like every other save a member makes here — and each changed election writes
        // its own labelled audit row on the server (AC-7).
        save.mutate(draft, { onSuccess: () => setSaved(true) });
      }}
    >
      <PageBackLink to={MEMBER_PROFILE_ROUTES.account} className="mb-[18px]">
        {MEMBER_ACCOUNT_COPY.title}
      </PageBackLink>

      <header className="mb-6">
        <h1 className="mb-1.5 text-2xl tracking-tight md:text-4xl">{MEMBER_PRIVACY_COPY.title}</h1>
        <p className="max-w-[700px] text-md text-muted-foreground md:text-lg">
          {MEMBER_PRIVACY_COPY.blurb}
        </p>
      </header>

      <Card className="mb-5 max-w-[900px]">
        {/* `padding:8px 24px` with `22px` on each row, so the first and last rows keep half a
            row's air above and below and the dividers land where the prototype draws them. */}
        <CardContent className="divide-y divide-divider px-6 py-2">
          {privacy.data.electionDefinitions.map((election) => {
            const id = election.id as keyof Elections;
            return (
              <div key={election.id} className="flex items-start gap-5 py-[22px]">
                <div className="min-w-0 flex-1">
                  <p className="font-brand text-md font-bold text-foreground md:text-lg">
                    {election.label}
                  </p>
                  <p className="mt-[3px] text-sm text-muted-foreground md:text-md">
                    {election.description}
                  </p>
                  {election.note === null ? null : (
                    // AC-2's distinction, said where the member is making the choice. Without it,
                    // somebody who thinks this hides the roster *from them* will not use it.
                    <p className="mt-2.5 text-sm text-brand md:text-md">{election.note}</p>
                  )}
                </div>
                <Switch
                  density="member"
                  aria-label={election.label}
                  checked={draft[id]}
                  onCheckedChange={(next) => {
                    setSaved(false);
                    setDraft((current) => (current === null ? current : { ...current, [id]: next }));
                  }}
                />
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Where a fourth toggle would sit. It is a sentence because the rule is not configurable —
          served from `platform-config`, never retyped here. */}
      <div className="mb-5 flex max-w-[900px] items-center gap-3.5 rounded-lg border border-brand-100 bg-brand-50 px-5 py-[18px]">
        <ShieldCheck aria-hidden className="h-[22px] w-[22px] flex-none text-brand" />
        <p className="text-sm leading-snug text-text-body md:text-md">
          {privacy.data.bidirectionalOptOutStatement}
        </p>
      </div>

      {/* AC-6 — the member's notification surface is exactly one election, and saying so is what
          keeps its smallness visible rather than merely true. */}
      <p className="mb-[22px] max-w-[900px] text-sm leading-relaxed text-muted-foreground md:text-md">
        {privacy.data.notificationScopeNote}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="submit"
          disabled={save.isPending}
          className="h-auto rounded-pill px-6 py-[14px] font-brand text-md font-bold"
        >
          {save.isPending ? 'Saving…' : MEMBER_PRIVACY_COPY.save}
        </Button>
        {saved ? (
          <span role="status" className="text-sm text-success">
            {MEMBER_PRIVACY_COPY.saved}
          </span>
        ) : null}
        {save.error !== null ? (
          <span role="alert" className="text-sm text-destructive">
            That did not save. Try again.
          </span>
        ) : null}
      </div>
    </form>
  );
}
